<template>
  <main class="page-operative page-operative-data-analysis page-operative-data-analysis-costs page-operative-data-analysis-costs-list d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-start justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Costi proporzionali</PageTitle>

        <app-router-link v-if="userCanEdit" class="btn btn-primary" :to="{ name: 'operative-company.data-analysis.cogs.add' }">
          Aggiungi costo proporzionale
        </app-router-link>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else>
          <div class="d-flex align-items-center justify-content-between">
            <ForecastListSwitcher :viewMode="viewMode" @update:viewMode="setViewMode" />

            <BFormCheckbox v-if="viewMode === 'forecast'" v-model="isSticky" class="no-select" :switch="true">
              Blocca aggregato in alto
            </BFormCheckbox>
          </div>

          <template v-if="viewMode === 'forecast' && data">
            <div class="overflow-auto report-texts-size">
              <OperativeTable dense sticky>
                <template v-for="(table, i) in aggregateTableRows">
                  <OperativeTableAggregateTable v-if="i === 0" :key="'aggregate-' + i" :sticky="isSticky">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years>{{ table.name }}</OperativeTableBodyHeader>
                    <OperativeTableBodyRow :values="table.data.importoPrevisto">
                      Importo previsto
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.importo">
                      Importo
                      <template #cell-content="{ value, col }">
                        <template v-if="table.data.parzialmenteConsuntivo[col]">
                          <em v-if="!table.data.consuntivo[col]">{{ value | money }}</em>
                          <span v-else>{{ value | money }}</span>
                        </template>
                        <template v-else><span></span></template>
                      </template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.deltaImporto">
                      Differenza su previsione
                      <template #cell-content="{ value, col }">
                        <span v-if="!table.data.parzialmenteConsuntivo[col]"></span>
                        <span v-else-if="value < 0" style="color:green;white-space:nowrap">{{ value | money }}</span>
                        <span v-else style="color:red;white-space:nowrap">+{{ value | money }}</span>
                      </template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.deltaImportoPerc">
                      Differenza su previsione (%)
                      <template #cell-content="{ value, col }">
                        <span v-if="!table.data.parzialmenteConsuntivo[col]"></span>
                        <span v-else-if="value < 0" style="color:green;white-space:nowrap">{{ value | percentage }}</span>
                        <span v-else style="color:red;white-space:nowrap">+{{ value | percentage }}</span>
                      </template>
                    </OperativeTableBodyRow>
                    <OperativeTableEmptyBodyRow v-if="i !== aggregateTableRows.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </OperativeTableAggregateTable>
                  <tbody v-else :key="'item-' + i">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" :show-years="openedRows[table.id]">
                      <p class="mb-0 px-1" @click="toggleRow(table.id)" style="user-select: none; cursor: pointer">
                        <i v-if="openedRows[table.id]" class="fas fa-caret-up"></i>
                        <i v-else class="fas fa-caret-down"></i>
                        {{ table.name }}
                      </p>
                    </OperativeTableBodyHeader>
                    <template v-if="openedRows[table.id]">
                      <OperativeTableBodyRow :values="table.data.importoAutomatico">
                        Importo Automatico
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.importoBudget">
                        Importo a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'budget', 'Importo a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                            <span>
                              {{ value | money }}
                            </span>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.importoConsuntivo">
                        Importo a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!table.data.enabledConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'consuntivo', 'Importo a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                            <span>
                              {{ value | money }}
                            </span>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.deltaImporto">
                        Differenza su previsione
                        <template #cell-content="{ value, col }">
                          <span v-if="!table.data.consuntivo[col]"></span>
                          <span v-else-if="value < 0" style="color:green;white-space:nowrap">{{ value | money }}</span>
                          <span v-else style="color:red;white-space:nowrap">+{{ value | money }}</span>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.deltaImportoPerc">
                        Differenza su previsione (%)
                        <template #cell-content="{ value, col }">
                          <span v-if="!table.data.consuntivo[col]"></span>
                          <span v-else-if="value < 0" style="color:green;white-space:nowrap">{{ value | percentage }}</span>
                          <span v-else style="color:red;white-space:nowrap">+{{ value | percentage }}</span>
                        </template>
                      </OperativeTableBodyRow>
                    </template>
                    <OperativeTableEmptyBodyRow v-if="i !== aggregateTableRows.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </tbody>
                </template>
              </OperativeTable>
            </div>

            <BModal :title="currentModalInfo.title" dialog-class="modal--type-dialog" id="edit-value-modal" ref="edit-value-modal" hide-footer centered scrollable>
              <form @submit.prevent="saveCurrentModalInfo">
                <div class="form-group row align-items-end py-4">
                  <div class="col">
                    <span class="font-weight-bold d-block">
                      {{ currentModalInfo.typeName }}
                    </span>
                    <span class="text-muted small">
                      Mensilità: {{ currentModalInfo.month }}/{{ currentModalInfo.year }}
                    </span>
                  </div>
                  <div class="col">
                    <input type="number" v-model="currentModalInfo.value" class="form-control col" placeholder="Inserisci importo">
                  </div>
                </div>
                <div class="text-right">
                  <button class="btn btn-primary" type="submit">Salva</button>
                </div>
              </form>
            </BModal>
          </template>
          <CostiPerRicaviListTable v-else-if="viewMode === 'list' && results" class="report-texts-size" :costi="results" :edit-enabled="userIsAdmin || userIsContextCompanyConsultant || userIsContextCompanyOwner" @delete="removeCostoPerRicavo" />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage';
import loadingDataMixin from '@/mixins/loadingData';
import computedFinancialDataMixin from '@/mixins/computedFinancialData';
import confirmWithModalMixin from '@/libs/Confirm/mixins/confirmWithModal';
import formatMoneyFilter from '@/filters/formatMoney';
import formatPercentageFilter from '@/filters/formatPercentage';

import { BFormCheckbox, BModal } from 'bootstrap-vue';

export default {
  name: 'page-operative-company-data-analysis-costs-list',
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin, confirmWithModalMixin],
  components: {
    BModal,
    BFormCheckbox,
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow'),
    OperativeTableEmptyBodyRow: () => import('@/views/components/Operative/OperativeTableEmptyBodyRow'),
    OperativeTableAggregateTable: () => import('@/views/components/Operative/OperativeTable/OperativeTableAggregateTable'),
    ForecastListSwitcher: () => import('@/views/components/Operative/ForecastListSwitcher'),
    CostiPerRicaviListTable: () => import('@/views/components/Operative/ListTable/CostiPerRicaviListTable'),
    AppRouterLink: () => import('@/views/components/Layout/AppRouterLink'),
  },
  filters: {
    money: formatMoneyFilter,
    percentage: formatPercentageFilter,
  },
  data () {
    return {
      data: null,
      results: null,
      viewMode: 'forecast',
      isSticky: true,
      currentModalInfo: {
        id: null,
        title: null,
        type: null,
        typeName: null,
        month: null,
        year: null,
        value: null,
      },
      openedRows: {},
    };
  },
  computed: {
    aggregateTableHeadings () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.convertToMonthsHeading(this.data[0].data);
    },
    aggregateTableRows () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.data.map(d => ({
        id: d.id,
        name: d.name,
        data: this.convertToRowGroup(d.data, [
          'importoAutomatico',
          'importoBudget',
          'importoConsuntivo',
          'importoPrevisto',
          'importo',
          'deltaImporto',
          'deltaImportoPerc',
          'parzialmenteConsuntivo',
          'consuntivo',
          'iva',
          'costoIvato',
          'ritenuta',
          'totale',
          'debito',
          'enabledBudgetEdit',
          'enabledConsuntivoEdit',
          'month',
          'year',
        ]),
      }));
    },
  },
  methods: {
    setViewMode (mode) {
      if (mode === 'forecast') {
        this.retrieveForecastData();
      } else if (mode === 'list') {
        this.retrieveListData();
      }
      this.viewMode = mode;
    },
    retrieveForecastData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getComputedCostiCogs(this.company?.id)
        .then(res => {
          this.data = res?.data?.data || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    retrieveListData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getCostiCogs(this.company?.id)
        .then(res => {
          this.results = res?.data?.costiCogs || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    showAddBudgetModal (id, title, type, typeName, month, year, value) {
      this.currentModalInfo.id = id;
      this.currentModalInfo.title = title;
      this.currentModalInfo.type = type;
      this.currentModalInfo.typeName = typeName;
      this.currentModalInfo.month = month;
      this.currentModalInfo.year = year;
      this.currentModalInfo.value = value;
      this.$refs['edit-value-modal'].show();
    },
    saveCurrentModalInfo () {
      this.$api.saveCostoCogsValue(this.company.id, this.currentModalInfo.id, this.currentModalInfo.type, this.currentModalInfo.year, this.currentModalInfo.month, this.currentModalInfo.value)
        .then(() => {
          this.$refs['edit-value-modal'].hide();
          this.setViewMode('forecast');
        })
        .catch(this.$log.errors)
      ;
    },
    removeCostoPerRicavo (id) {
      this.confirm('Cancellare il costo proporzionale selezionato? L\'operazione è irreversibile', 'Ok', 'Annulla')
        .then(confirmed => {
          if (!confirmed) {
            return;
          }

          this.$api.deleteCostoCogs(this.company.id, id).then(() => this.retrieveListData()).catch(this.$log.error);
        })
      ;
    },
    toggleRow (id) {
      if (undefined === this.openedRows[id]) {
        this.$set(this.openedRows, id, true);

        return;
      }

      this.$set(this.openedRows, id, !this.openedRows[id]);
    },
  },
  watch: {
    company: {
      immediate: true,
      handler: function () {
        this.setViewMode('forecast');
      },
    },
  },
};

</script>
